import React from 'react';

import { useSnackbar } from 'notistack';
import { useParams } from 'react-router-dom';

import { Alert, Container } from '@mui/material';

import PageHeader from '~/components/Common/Elements/PageHeader';

import useAppNavigation from '~/hooks/useAppNavigation';
import useRecentlyViewedProduct from '~/hooks/useRecentlyViewedProduct';

import { toDayJs } from '~/services/TimeService';

import { useCreateFlightDeal, useDeleteFlightDeal, useFlightDeal, useUpdateFlightDeal } from '../hooks/useFlightDeal';

import { FlightDealsForm } from './FlightDealsForm';

export default function FlightsDealsEdit() {
  const { id } = useParams<{ id: string }>();
  const { replace } = useAppNavigation();
  const { enqueueSnackbar } = useSnackbar();

  const { data, error, loading } = useFlightDeal(id);
  const { mutate: updateDeal, loading: updating, error: updateError } = useUpdateFlightDeal();
  const { mutate: deleteDeal, loading: deleting } = useDeleteFlightDeal();
  const { mutateAsync: createDeal, loading: cloning } = useCreateFlightDeal();

  useRecentlyViewedProduct({
    id: id ? `flights:deal:${id}` : undefined,
    label: data?.dealName,
    productLabel: 'Flights',
  });

  const handleDelete = () => {
    deleteDeal(data.id, {
      onSuccess: () => {
        replace('/flights?tab=deals');
      },
    });
  };

  const handleClone = async () => {
    const offerDurations = data.offerDuration.filter((duration) => duration.departureDate !== null);

    try {
      const { result } = await createDeal({
        carrierCode: data.carrierCode,
        region: data.region,
        originAirportCode: data.originAirportCode,
        destinationAirportCode: data.destinationAirportCode,
        fareType: data.fareType,
        perAdultPrice: parseFloat(data.perAdultPrice),
        luxPlusPerAdultPrice: data.luxPlusPerAdultPrice ?? null,
        salesStartDate: data.salesStartDate,
        salesEndDate: data.salesEndDate,
        dealName: data.dealName,
        heroImagePublicId: data.heroImagePublicId,
        categoryGroup: data.categoryGroup,
        fareClass: data.fareClass,
        bookingClassCode: data.bookingClassCode,
        adultCount: data.adultCount,
        discountLabel: data.discountLabel,
        searchText: 'View flights',
        dealDescription: data.dealDescription,
        fareName: '',
        fareBasis: '',
        termsAndConditions: data.termsAndConditions,
        travelDays: data.travelDays,
        hierarchy: data.hierarchy,
        offerDurations,
        airlineCampaign: data.airlineCampaign,
        destinationCampaign: data.destinationCampaign,
        departureAirportCampaign: data.departureAirportCampaign,
      });

      if (result.id) {
        window.open(`/flights/flight-deal/${result.id}/edit`, '_blank');
        enqueueSnackbar('Deal was cloned', { variant: 'success' });
      } else {
        enqueueSnackbar('Deal was cloned but failed to open - please refresh the page', { variant: 'warning' });
      }
    } catch {
      enqueueSnackbar('Something went wrong, please try again', { variant: 'error' });
    }
  };

  const showForm = !error && !loading && data;

  return (
    <Container>
      <PageHeader title="Edit Deal" backButton />

      {error && <Alert severity="error">Something went wrong, please try again</Alert>}

      {showForm && (
        <FlightDealsForm
          onSubmit={(formData) => updateDeal({ id, data: formData })}
          onDelete={handleDelete}
          onClone={handleClone}
          loading={updating}
          error={updateError ? 'Something went wrong, please try again' : undefined}
          initialValues={{
            adultCount: data.adultCount,
            bookingClassCode: data.bookingClassCode,
            carrierCode: data.carrierCode,
            categoryGroup: data.categoryGroup,
            dealDescription: data.dealDescription,
            dealName: data.dealName,
            departureDate1: toDayJs(data.offerDuration[0].departureDate),
            departureDate2: data.offerDuration[1].departureDate ? toDayJs(data.offerDuration[1]?.departureDate) : null,
            departureDate3: data.offerDuration[2].departureDate ? toDayJs(data.offerDuration[2]?.departureDate) : null,
            arrivalDate1: data.offerDuration[0]?.arrivalDate ? toDayJs(data.offerDuration[0].arrivalDate) : null,
            arrivalDate2: data.offerDuration[1]?.arrivalDate ? toDayJs(data.offerDuration[1]?.arrivalDate) : null,
            arrivalDate3: data.offerDuration[2]?.arrivalDate ? toDayJs(data.offerDuration[2]?.arrivalDate) : null,
            destinationAirportCode: data.destinationAirportCode,
            discountLabel: data.discountLabel,
            fareClass: data.fareClass,
            fareType: data.fareType,
            hierarchy: data.hierarchy?.toString() ?? '0',
            originAirportCode: data.originAirportCode,
            heroImage: data.heroImagePublicId,
            perAdultPrice: data.perAdultPrice?.toString(),
            luxPlusPerAdultPrice: data.luxPlusPerAdultPrice?.toString() ?? '',
            region: data.region,
            termsAndConditions: data.termsAndConditions,
            travelDays:
              data.travelDays && Array.isArray(data.travelDays)
                ? data.travelDays.join(',')
                : (data.travelDays as string),
            salesStartDate: toDayJs(data.salesStartDate),
            salesEndDate: toDayJs(data.salesEndDate),
            departureAirportCampaign: data.departureAirportCampaign,
            airlineCampaign: data.airlineCampaign,
            destinationCampaign: data.destinationCampaign,
          }}
          deleting={deleting}
          cloning={cloning}
          showClone
          showDelete
        />
      )}
    </Container>
  );
}
